/* Velcroy x Atelier: motion system, floating widgets and bundle default.
   - Reveals: .vlc-reveal (fade up), .vlc-wipe (image unveil), .vlc-split (headings),
     .vlc-line-draw. Theme elements (product cards, section headings, footer) are opted in here;
     anything already on screen at load is left alone so nothing blinks. (velcroy-refine.css keeps
     all of these to a quiet fade.)
   - Parallax: large editorial images (story bands, page banners) drift gently while scrolling.
   - Floating widgets: the Inbox chat button and Shopify Forms teaser are scaled down inside their
     shadow roots (chat becomes icon-only on phones), and fade out while the footer is in view
     (html.vlc-near-footer).
   Atelier scrolls inside .page-wrapper, so scroll work listens there as well as on window.
   (Gift wrapping wires itself up in snippets/velcroy-gift-note.liquid.) */
(function () {
  window.vlcMotion = true;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var PARALLAX = '.vlc-story__media img, .vlc-pdpstory__media img, .vlc-pagehero--banner .vlc-pagehero__media img';
  var io = null;

  function inView(el) {
    var r = el.getBoundingClientRect();
    return r.top < window.innerHeight * 0.92 && r.bottom > 0;
  }

  function observe(el) {
    if (reduce || !io) { el.classList.add('is-in'); return; }
    io.observe(el);
  }

  function splitWords(el) {
    if (el.dataset.vlcSplit || el.children.length) return false;
    var words = el.textContent.trim().split(/\s+/);
    el.textContent = '';
    words.forEach(function (word, i) {
      var outer = document.createElement('span');
      outer.className = 'vlc-w';
      var inner = document.createElement('span');
      inner.textContent = word;
      outer.style.setProperty('--vlc-i', i);
      outer.appendChild(inner);
      el.appendChild(outer);
      if (i < words.length - 1) el.appendChild(document.createTextNode(' '));
    });
    el.dataset.vlcSplit = 'true';
    el.setAttribute('aria-label', words.join(' '));
    return true;
  }

  function optIn(selector, cls, opts) {
    document.querySelectorAll(selector).forEach(function (el) {
      if (el.dataset.vlcMotion) return;
      el.dataset.vlcMotion = 'true';
      if (opts && opts.skipInView && inView(el)) return;
      if (cls === 'vlc-split' && !splitWords(el)) return;
      el.classList.add(cls);
      if (opts && opts.stagger) {
        var idx = Array.prototype.indexOf.call(el.parentElement.children, el);
        el.style.setProperty('--vlc-delay', Math.min(idx, 6) * opts.stagger + 'ms');
      }
      observe(el);
    });
  }

  function initMotion() {
    if (!reduce && 'IntersectionObserver' in window && !io) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.06 });
    }

    // Velcroy sections already carry their classes in Liquid.
    document.querySelectorAll('.vlc-reveal, .vlc-wipe, .vlc-line-draw').forEach(function (el) {
      if (el.dataset.vlcMotion) return;
      el.dataset.vlcMotion = 'true';
      observe(el);
    });
    optIn('.vlc-heading, .vlc-pagehero__title, .vlc-article__title', 'vlc-split', { skipInView: false });

    // Theme elements.
    optIn('main .text-block h2, main .text-block h3:only-child', 'vlc-split', { skipInView: true });
    optIn('main product-card, main .product-grid__item, main .resource-list__item', 'vlc-reveal', { skipInView: true, stagger: 70 });
    optIn('main .accordion, .product-recommendations .text-block', 'vlc-reveal', { skipInView: true });
    optIn('footer .group-block-content > *, footer .footer-utilities > *', 'vlc-reveal', { skipInView: true, stagger: 90 });
  }

  /* ---------- Parallax ---------- */
  var parallaxEls = [];
  var ticking = false;
  var listening = false;

  function updateParallax() {
    ticking = false;
    var vh = window.innerHeight;
    parallaxEls.forEach(function (img) {
      var box = img.parentElement.getBoundingClientRect();
      if (box.bottom < -100 || box.top > vh + 100) return;
      var offset = (box.top + box.height / 2 - vh / 2) * -0.05;
      img.style.transform = 'translate3d(0,' + offset.toFixed(1) + 'px,0)';
    });
  }

  function requestParallax() {
    if (!ticking) { ticking = true; requestAnimationFrame(updateParallax); }
  }

  function initParallax() {
    if (reduce || window.matchMedia('(max-width: 749px)').matches) return;
    parallaxEls = Array.prototype.slice.call(document.querySelectorAll(PARALLAX));
    if (!parallaxEls.length) return;
    if (!listening) {
      listening = true;
      var scroller = document.querySelector('.page-wrapper');
      if (scroller) scroller.addEventListener('scroll', requestParallax, { passive: true });
      window.addEventListener('scroll', requestParallax, { passive: true });
      window.addEventListener('resize', requestParallax);
    }
    updateParallax();
  }

  /* ---------- Floating widgets ----------
     Both apps render into open shadow roots after load, so keep checking for a few seconds and
     add one small style element to each. Hashed Forms class names are matched by prefix, and
     !important is needed because the apps' own rules are more specific. */
  var WIDGET_CSS = {
    'shopify-chat':
      '.activator-nudge { transform: scale(0.8) !important; transform-origin: 100% 100% !important; }' +
      '@media (max-width: 749px) {' +
      ' .activator-nudge { transform: scale(0.72) !important; }' +
      ' .activator__label { display: none !important; }' +
      ' .activator { padding-inline: 12px !important; gap: 0 !important; }' +
      '}',
    'shopify-forms-embed':
      '[class*="_appEmbedRoot_"] { transform: scale(0.78) !important; transform-origin: 0 100% !important; }' +
      '@media (max-width: 749px) { [class*="_appEmbedRoot_"] { transform: scale(0.68) !important; } }'
  };

  function styleWidgets() {
    var pending = false;
    Object.keys(WIDGET_CSS).forEach(function (tag) {
      document.querySelectorAll(tag).forEach(function (host) {
        var root = host.shadowRoot;
        if (!root) { pending = true; return; }
        if (root.querySelector('style[data-vlc]')) return;
        var style = document.createElement('style');
        style.setAttribute('data-vlc', '');
        style.textContent = WIDGET_CSS[tag];
        root.appendChild(style);
      });
      if (!document.querySelector(tag)) pending = true;
    });
    return pending;
  }

  function initWidgets() {
    var tries = 0;
    var timer = setInterval(function () {
      tries += 1;
      if (!styleWidgets() || tries > 20) clearInterval(timer);
    }, 500);

    var footer = document.querySelector('footer');
    if (!footer || !('IntersectionObserver' in window)) return;
    new IntersectionObserver(function (entries) {
      document.documentElement.classList.toggle('vlc-near-footer', entries[0].isIntersecting);
    }, { threshold: 0 }).observe(footer);
  }

  /* ---------- Bundle widget: start on "Single" ----------
     The Super Subscriptions & Bundles block preselects "Double", which silently adds 2 bottles.
     Keep nudging the first tier for a few seconds after load, but never once the shopper has
     picked a tier, and never move focus or scroll the page. */
  var shopperPickedTier = false;
  document.addEventListener('click', function (e) {
    if (e.isTrusted && e.target.closest && e.target.closest('.sss-bundle-block__tier-row, .sss-bundle-block__radio')) shopperPickedTier = true;
  }, true);

  function selectSingleTier() {
    if (shopperPickedTier) return;
    var single = document.querySelector('.sss-bundle-block__radio[value="0"]');
    if (!single || single.checked) return;
    var scroller = document.querySelector('.page-wrapper');
    var top = scroller ? scroller.scrollTop : 0;
    var previous = document.activeElement;
    (single.closest('label') || single).click();
    if (document.activeElement === single) {
      single.blur();
      if (previous && previous !== document.body && typeof previous.focus === 'function') previous.focus({ preventScroll: true });
    }
    if (scroller && scroller.scrollTop !== top) scroller.scrollTop = top;
  }

  function enforceSingleFor(ms) {
    var until = Date.now() + ms;
    var timer = setInterval(function () {
      selectSingleTier();
      if (shopperPickedTier || Date.now() > until) clearInterval(timer);
    }, 250);
  }

  function watchBundle() {
    if (!document.querySelector('[data-template="product"]')) return;
    enforceSingleFor(6000);
    window.addEventListener('load', function () { enforceSingleFor(3000); });
    document.addEventListener('change', function (e) {
      if (e.target.closest && e.target.closest('variant-picker')) enforceSingleFor(2000);
    });
  }

  function init() {
    initMotion();
    initParallax();
    initWidgets();
    watchBundle();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', function () { initMotion(); initParallax(); });
  // Cards that arrive later (filters, pagination, recommendations) get the same entrance.
  if ('MutationObserver' in window) {
    var pending = false;
    new MutationObserver(function () {
      if (pending) return;
      pending = true;
      setTimeout(function () { pending = false; initMotion(); }, 120);
    }).observe(document.querySelector('main') || document.body, { childList: true, subtree: true });
  }
})();